import { OutlinedButton } from '@components/Button/OutlinedButton';
import { faCircleX } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCancelCadJob } from '@pages/CreateNewProductPage/ProcessingPage/useCancelCadJob';
import { useProcessingFileTimeoutHandler } from '@pages/CreateNewProductPage/ProcessingPage/useProcessingFileTimeoutHandler';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Redirect, useLocation } from 'react-router-dom';
import { LoadingSpinner } from 'src/components/Loading';
import { CADErrorState } from 'src/components/ProcessingFile/CADErrorState';
import { CADProcessingState } from 'src/components/ProcessingFile/CADProcessingState';
import {
  FooterContainer,
  ProcessingFileContent,
  StyledCancelButtonContainer,
} from 'src/components/ProcessingFile/styles';
import { Toast } from 'src/components/Toast';
import { useTenant } from 'src/providers/Tenant';
import { getCADJob } from 'src/services/productInventory';

export function ProcessingFilePage() {
  const location = useLocation();

  const { t } = useTranslation('createProduct');
  const { accountId } = useTenant();

  const searchParams = new URLSearchParams(location?.search);
  const runID = searchParams.get('runID');
  const productName = searchParams.get('productName');

  const {
    isError,
    isLoading: isGETJobLoading,
    data: runInfo,
  } = useQuery(['getCadRunInfo', runID], /* istanbul ignore next */ () => getCADJob(runID!, accountId), {
    refetchInterval: 10000,
    enabled: Boolean(runID),
    notifyOnChangeProps: 'all',
  });

  const { cancelJob } = useCancelCadJob();

  useProcessingFileTimeoutHandler(runInfo, accountId);

  if (runInfo?.status === 'CANCELLED_BY_USER') {
    return <Redirect to="/create-product/cad" />;
  }

  if (runInfo?.status === 'IN_PROGRESS_REQUIRES_ATTRIBUTE_MAPPING') {
    return (
      <Redirect
        to={`/create-product/cad/select-part-number?productName=${productName}&configurationPID=${runInfo.configurationPID}&runID=${runID}`}
      />
    );
  }

  if (isGETJobLoading) {
    return (
      <>
        <ProcessingFileContent data-testid="processing-file">
          <LoadingSpinner data-testid="loading-spinner" />
        </ProcessingFileContent>
        <FooterContainer />
      </>
    );
  }

  return (
    <>
      <ProcessingFileContent data-testid="processing-file">
        {runInfo?.status.startsWith('IN_PROGRESS_') && runInfo.status !== 'IN_PROGRESS_CAD_PROCESSING_FAILED' && (
          <CADProcessingState productName={runInfo?.productName}>
            <StyledCancelButtonContainer>
              <OutlinedButton
                color="error"
                endIcon={<FontAwesomeIcon icon={faCircleX} />}
                onClick={() => cancelJob(runID!, accountId)}
              >
                {t('cancelAndRestart')}
              </OutlinedButton>
            </StyledCancelButtonContainer>
          </CADProcessingState>
        )}
        {/* IN_PROGRESS_CAD_PROCESSING_FAILED and FAILED, can directly check for FAILED when changeover of statuses is completed */}
        {runInfo?.status.includes('FAILED') && (
          <CADErrorState productName={runInfo?.productName} runID={runInfo?.runID} />
        )}
      </ProcessingFileContent>
      <FooterContainer />
      <Toast
        dependency={isError}
        severity="error"
        title={`${t('errorProcessingFile')}`}
        data-testid="toast-notification"
      />
    </>
  );
}
